/** @file Appends one pixel-space primitive (vertices, uvs, colours, local indices) to the sink. */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { affine } from '@pose2d/math/affine';
import type { Sink } from './createSink';
import { reserveRoom } from './reserveRoom';

const VW = RENDER_LAYOUT.VERTEX_WORDS;
const DS = RENDER_LAYOUT.DRAW_STRIDE;
const p: [number, number] = [0, 0];

/**
 * One primitive in pixel space (y down). Emitters keep one of these per file and refill it for every primitive (no
 * object per primitive); `light` is always a number and `lights` the optional per-vertex array, so the field holding
 * a colour never changes type.
 */
export interface PixelMesh {
  page: number;
  /** Vertex count; xy (and uv when given) hold 2 numbers per vertex. */
  count: number;
  xy: ArrayLike<number>;
  /** Absolute page UVs, or null for the page centre (0.5, 0.5), i.e. the 1x1 white page. */
  uv: ArrayLike<number> | null;
  /** The light word of every vertex (`math/color.pack`, straight alpha) when `lights` is null. */
  light: number;
  /** One light word per vertex, or null to use `light` for all of them. */
  lights: ArrayLike<number> | null;
  /** Triangle indices local to this primitive. */
  indices: ArrayLike<number>;
}

/** True when pixel (x, y) maps to skeleton coordinates float32 can hold (`fround` overflows to Infinity, NaN stays). */
function storable(sink: Sink, x: number, y: number): boolean {
  affine.apply(sink.toWorld, x, y, p);
  return Number.isFinite(Math.fround(p[0])) && Number.isFinite(Math.fround(p[1]));
}

/**
 * True when the inverse camera is finite and every vertex maps to a float32-representable skeleton point (raster
 * rejects non-finite vertices). A singular camera makes `sink.toWorld` NaN/Infinity, a near-singular one or a huge
 * pixel coordinate overflows float32 on store: either way the primitive is dropped whole.
 */
function finite(sink: Sink, mesh: PixelMesh): boolean {
  for (let i = 0; i < 6; i++) if (!Number.isFinite(sink.toWorld[i] ?? NaN)) return false;
  for (let i = 0; i < mesh.count; i++) if (!storable(sink, mesh.xy[2 * i] ?? NaN, mesh.xy[2 * i + 1] ?? NaN)) return false;
  return true;
}

/** Grows [minX, minY, maxX, maxY] to include (x, y). */
function grow(b: Float32Array, x: number, y: number): void {
  b[0] = Math.min(b[0] ?? x, x);
  b[1] = Math.min(b[1] ?? y, y);
  b[2] = Math.max(b[2] ?? x, x);
  b[3] = Math.max(b[3] ?? y, y);
}

/** Writes vertex `i` of `mesh` into slot `v` of `chunk`, mapped to skeleton space, and grows the bounds. */
function writeVertex(chunk: RenderBuffers, sink: Sink, v: number, mesh: PixelMesh, i: number): void {
  affine.apply(sink.toWorld, mesh.xy[2 * i] ?? 0, mesh.xy[2 * i + 1] ?? 0, p);
  const o = v * VW, f = chunk.vertexF32;
  f[o] = p[0];
  f[o + 1] = p[1];
  f[o + 2] = mesh.uv === null ? 0.5 : (mesh.uv[2 * i] ?? 0);
  f[o + 3] = mesh.uv === null ? 0.5 : (mesh.uv[2 * i + 1] ?? 0);
  chunk.vertexU32[o + 4] = mesh.lights === null ? mesh.light : (mesh.lights[i] ?? 0);
  chunk.vertexU32[o + 5] = 0;
  grow(chunk.bounds, p[0], p[1]);
}

/** Emits the primitive into the current draw (opening chunks and draws as needed); skips it when not finite. */
export function emitMesh(sink: Sink, mesh: PixelMesh): void {
  if (!finite(sink, mesh)) return;
  const n = mesh.indices.length;
  const chunk = reserveRoom(sink, mesh.page, mesh.count, n);
  const base = chunk.vertexCount;
  for (let i = 0; i < mesh.count; i++) writeVertex(chunk, sink, base + i, mesh, i);
  for (let k = 0; k < n; k++) chunk.indices[chunk.indexCount + k] = base + (mesh.indices[k] ?? 0);
  chunk.vertexCount += mesh.count;
  chunk.indexCount += n;
  const d = (chunk.drawCount - 1) * DS;
  chunk.draws[d + 3] = (chunk.draws[d + 3] ?? 0) + n;
  chunk.draws[d + 5] = (chunk.draws[d + 5] ?? 0) + mesh.count;
}
